import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useJourneyAuth } from "../auth/AuthContext";
import { MANUAL_ROLES } from "../auth/session";
import { AGENT_LOGIN_URL, BROKER_LOGIN_URL, appUrl } from "../config";

const DESKS = [
  { role: "buyer", title: "Buyers", href: appUrl("/login"), note: "Open the Buyer’s Checklist on REChecklist." },
  { role: "seller", title: "Sellers", href: appUrl("/seller/login"), note: "Open the Seller’s Checklist — never mixed with buyer state." },
  { role: "agent", title: "Agents", href: AGENT_LOGIN_URL, note: "Open Agent Desk: files, messaging, calendar." },
  { role: "broker", title: "Brokers", href: BROKER_LOGIN_URL, note: "Open Broker Desk: supervision and retention." },
];

export default function Login() {
  const { login, session, logout } = useJourneyAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const intended = MANUAL_ROLES.includes(params.get("for")) ? params.get("for") : "";
  const [form, setForm] = useState({
    role: intended || "buyer",
    email: "",
    password: "",
  });
  const [error, setError] = useState("");

  const nextPath = useMemo(() => {
    if (intended) return `/manuals/${intended}`;
    return "/manuals";
  }, [intended]);

  function onRole(role) {
    setForm({ role, email: "", password: "" });
    setError("");
  }

  function onSubmit(event) {
    event.preventDefault();
    const result = login(form);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    const dest = result.session.role === "admin" ? "/manuals" : `/manuals/${result.session.role}`;
    navigate(intended && result.session.role === "admin" ? `/manuals/${intended}` : dest);
  }

  return (
    <article className="prose-page login-page">
      <h1>Sign in</h1>
      <p className="lead">
        User manuals for REChecklist live here, behind this door. The working desk still lives on
        REChecklist.pro.
      </p>

      {session ? (
        <p className="confirm">
          Signed in as {session.name} ({session.role}).{" "}
          <Link to={nextPath}>Open your manual</Link>
          {" · "}
          <button type="button" className="text-btn" onClick={logout}>
            Sign out
          </button>
        </p>
      ) : null}

      <form className="login-form" onSubmit={onSubmit}>
        <fieldset>
          <legend>Your role</legend>
          <div className="role-pills">
            {MANUAL_ROLES.map((role) => (
              <label key={role} className={form.role === role ? "is-on" : ""}>
                <input
                  type="radio"
                  name="role"
                  value={role}
                  checked={form.role === role}
                  onChange={() => onRole(role)}
                />
                {role[0].toUpperCase() + role.slice(1)}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={form.email}
            onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
            autoComplete="username"
          />
        </label>
        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={form.password}
            onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
            autoComplete="current-password"
          />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <button className="btn" type="submit">
          Open my manual
        </button>
      </form>

      <section className="login-desks">
        <h2>Open the working desk</h2>
        <p>Accounts for the checklist and the desks live on REChecklist. These doors go there.</p>
        <ul className="door-list">
          {DESKS.map((desk) => (
            <li key={desk.role}>
              <a href={desk.href}>{desk.title}</a>
              <span>{desk.note}</span>
            </li>
          ))}
        </ul>
      </section>
    </article>
  );
}
